import { useEffect, useRef, useState } from 'react';
import { Size } from '../constants';

interface UseNavbarOverrideOptions {
  isEditing: boolean;
  delayMs?: number;
}

interface UseNavbarOverrideReturn {
  navbarSizeOverride: Size | null;
  isNavbarButtonsDisabled: boolean;
}

const TOP_AREA_THRESHOLD = 200; // pixels from the top of the viewport to trigger the navbar expansion

export const NAVBAR_MOUSE_TRACKING_EVENT = 'updateNavbarMouseTracking';

// Any element (or descendant of an element) carrying this attribute suppresses the
// navbar expansion while the cursor is over it. Use it on top-of-page controls that
// would otherwise be shoved out from under the cursor when the navbar animates in.
export const NAVBAR_OVERRIDE_IGNORE_ATTR = 'data-navbar-override-ignore';

// The dead zone extends this far (px) beyond each opted-out element's box so the
// navbar stays collapsed as the cursor *approaches* the control, not just once it's
// dead on it — closing the awkward flicker band. Purely a hit-test margin; nothing
// visual changes. `bottom` is larger because the navbar animating in pushes the
// control downward, so we want to keep covering where it's headed.
const IGNORE_MARGIN = { top: 80, right: 60, bottom: 140, left: 60 };

// True if the point is within any opted-out element's bounding box, inflated by the
// margins above.
const isPointNearIgnoredElement = (x: number, y: number): boolean => {
  const elements = Array.from(document.querySelectorAll(`[${NAVBAR_OVERRIDE_IGNORE_ATTR}]`));
  for (const el of elements) {
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue; // skip hidden elements
    if (
      x >= r.left - IGNORE_MARGIN.left &&
      x <= r.right + IGNORE_MARGIN.right &&
      y >= r.top - IGNORE_MARGIN.top &&
      y <= r.bottom + IGNORE_MARGIN.bottom
    ) {
      return true;
    }
  }
  return false;
};

export const useNavbarOverride = ({ isEditing, delayMs }: UseNavbarOverrideOptions): UseNavbarOverrideReturn => {
  const [navbarSizeOverride, setNavbarSizeOverride] = useState<Size | null>(null);
  const [isNavbarButtonsDisabled, setIsNavbarButtonsDisabled] = useState<boolean>(false);
  const prevNavbarSizeOverrideRef = useRef<Size | null>(navbarSizeOverride);
  const [disableMouseTracking, setDisableMouseTracking] = useState(false);

  // custom event listener to disable mouse tracking
  useEffect(() => {
    const handleDisableMouseTracking = (e: CustomEvent<boolean>) => {
      setDisableMouseTracking(e.detail);
    };

    window.addEventListener(NAVBAR_MOUSE_TRACKING_EVENT, handleDisableMouseTracking as EventListener);

    return () => {
      window.removeEventListener(NAVBAR_MOUSE_TRACKING_EVENT, handleDisableMouseTracking as EventListener);
    };
  }, []);

  // Handle mouse movement to override navbar size
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    const handleMouseMove = (e: MouseEvent) => {
      // Don't expand the navbar while the cursor is on (or approaching) an opted-out
      // element, otherwise the animation can shift a top-of-page button out from under
      // the cursor mid-click. The margin closes the flicker band on approach.
      const isNearTop = e.clientY < TOP_AREA_THRESHOLD && !isPointNearIgnoredElement(e.clientX, e.clientY);
      const targetSize = isNearTop ? Size.md : null;

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        setNavbarSizeOverride(targetSize);
      }, delayMs || 0);
    };

    // Only attach the listener when not editing
    if (!isEditing && !disableMouseTracking) {
      window.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [delayMs, isEditing, disableMouseTracking]);

  // Handle navbar buttons disabled state
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    const prevNavbarSizeOverride = prevNavbarSizeOverrideRef.current;

    if (navbarSizeOverride !== prevNavbarSizeOverride && !isEditing) {
      setIsNavbarButtonsDisabled(navbarSizeOverride !== null);

      if (navbarSizeOverride) {
        timeoutId = setTimeout(() => setIsNavbarButtonsDisabled(false), 500);
      } else {
        setIsNavbarButtonsDisabled(false);
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }
    }

    prevNavbarSizeOverrideRef.current = navbarSizeOverride;

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [navbarSizeOverride, isEditing]);

  return {
    navbarSizeOverride,
    isNavbarButtonsDisabled: isNavbarButtonsDisabled && !isEditing,
  };
};
